import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import usePagebuilder from '../../../hooks/pagebuilder/usePagebuilder';
import { ALL_COMPONENTS_INTERSECTION_TYPE, UiComponent } from '../../../interfaces/pagebuilder';
import { useSelector } from '../../../reduxToolkit/typedRedux';
import { ConditionalWrapper } from '../../common/ConditionalWrapper';
import { SizeTemplateControl } from './SizeTemplateControl';

interface AdjustableSizeWrapperProps {
  uiComponent?: ALL_COMPONENTS_INTERSECTION_TYPE | UiComponent | undefined;
  showControl?: boolean;
  children: any;
}

export const AdjustableSizeWrapper = (props: AdjustableSizeWrapperProps) => {
  const { uiComponent, showControl } = props;

  const templateEditMode = useSelector(({ common }) => common.templateEditMode);

  const { editComponent } = usePagebuilder();

  const innerRef = useRef<HTMLDivElement>(null);

  const [topPaddingPercentage, setTopPaddingPercentage] = useState<number>();
  const [bottomPaddingPercentage, setPaddingBottomPercentage] = useState<number>();
  /**
   * We have our own showControl State here to ensure the borders are visible also during drag
   */
  const [isResizing, setIsResizing] = useState(false);

  function getCurrentPadding(direction: 'top' | 'bottom') {
    if (!innerRef.current) return 0;
    if (direction === 'top') return innerRef.current?.style?.paddingTop ? parseFloat(innerRef.current?.style?.paddingTop) : 0;
    return innerRef.current?.style?.paddingBottom ? parseFloat(innerRef.current?.style?.paddingBottom) : 0;
  }

  function getClientHeight() {
    if (!innerRef.current) return 0;
    const currentTop = getCurrentPadding('top');
    const currentBottom = getCurrentPadding('bottom');
    const cleanClientHeight = innerRef.current?.clientHeight - currentTop - currentBottom;

    return cleanClientHeight;
  }

  useEffect(() => {
    if (uiComponent) {
      setTopPaddingPercentage(uiComponent?.styling?.topPaddingPercentage ?? 0);
      setPaddingBottomPercentage(uiComponent?.styling?.bottomPaddingPercentage ?? 0);
    }
  }, [uiComponent]);

  useLayoutEffect(() => {
    if (!innerRef.current || topPaddingPercentage === undefined || bottomPaddingPercentage === undefined) return;
    const cleanClientHeight = getClientHeight();
    innerRef.current.style.paddingTop = `${topPaddingPercentage * cleanClientHeight}px`;
    innerRef.current.style.paddingBottom = `${bottomPaddingPercentage * cleanClientHeight}px`;
  }, [innerRef, innerRef.current?.clientHeight, topPaddingPercentage, bottomPaddingPercentage, templateEditMode]);

  function _calculateValue(yPosition: number, direction: 'top' | 'bottom', cleanClientHeight: number) {
    if (!innerRef.current) return 0;

    if (direction === 'top') return Math.abs(yPosition - innerRef.current.getBoundingClientRect().top);

    return yPosition - innerRef.current.getBoundingClientRect().top - cleanClientHeight;
  }

  function handleResize(yPosition: number, direction: 'top' | 'bottom') {
    !isResizing && setIsResizing(true);
    calculateAndSetPercentageBasedOnPosition(yPosition, direction);
  }

  function calculateAndSetPercentageBasedOnPosition(yPosition: number, direction: 'top' | 'bottom') {
    if (!innerRef.current) return;
    const cleanClientHeight = getClientHeight();
    const value = _calculateValue(yPosition, direction, cleanClientHeight);
    const percentage = Math.max(value / cleanClientHeight, 0);
    direction === 'top' ? setTopPaddingPercentage(percentage) : setPaddingBottomPercentage(percentage);
    return percentage;
  }

  function stopResize(yPosition: number, direction: 'top' | 'bottom') {
    setIsResizing(false);
    const percentage = calculateAndSetPercentageBasedOnPosition(yPosition, direction);
    const prop = direction === 'top' ? { topPaddingPercentage: percentage } : { bottomPaddingPercentage: percentage };
    const _component = { ...uiComponent, styling: { ...uiComponent?.styling, ...prop } };
    editComponent(_component);
  }

  return (
    <div
      className={`relative w-full p-0.5 flex justify-center`}
      style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', minHeight: 'max-content' }}
      ref={innerRef}>
      <ConditionalWrapper
        condition={templateEditMode === true && !uiComponent?.columnComponent}
        wrapper={(children) => (
          <SizeTemplateControl
            uiComponent={uiComponent}
            showControl={showControl || isResizing}
            onResizeTop={(position) => handleResize(position, 'top')}
            onResizeBottom={(position) => handleResize(position, 'bottom')}
            onStopResizeBottom={(position) => stopResize(position, 'bottom')}
            onStopResizeTop={(position) => stopResize(position, 'top')}>
            {children}
          </SizeTemplateControl>
        )}>
        {props.children}
      </ConditionalWrapper>
    </div>
  );
};
